// src/app/weather-widget/parts/weather-middle.component.ts
import { Component, ElementRef, ViewChild, OnDestroy, inject, signal, effect, AfterViewInit, ChangeDetectionStrategy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Chart, LineController, LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Filler, ChartOptions } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { WeatherStore } from '../../services/weather.store';
import { TempUnit } from '@/app/models/weather-widget.model';
import { TranslateModule } from '@ngx-translate/core';

Chart.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Filler, ChartDataLabels);

@Component({
  selector: 'dr-flex-home-ui-weather-middle',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './weather-middle.component.html',
  styleUrls: ['./weather-middle.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WeatherMiddleComponent implements OnDestroy, AfterViewInit {
  store = inject(WeatherStore);
  protected readonly TempUnit = TempUnit;
  @ViewChild('chartCanvas', { static: true })
  canvas!: ElementRef<HTMLCanvasElement>;
  private chart?: Chart;

  /** Turns true after view init */
  private viewReady = signal(false);

  constructor() {
    // Create effect IN an injection context
    effect(() => {
      const ready = this.viewReady(); // becomes true after ngAfterViewInit
      const series = this.store.hourlySeries(); // reacts to data/day/tab/unit/city

      if (!ready || !this.canvas || !series.labels.length) return;
      this.render(series.labels, series.values);
    });
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.chart?.resize();
  }

  ngAfterViewInit() {
    // Now the canvas exists; let the effect run and render the initial chart
    this.viewReady.set(true);
  }

  ngOnDestroy() {
    this.chart?.destroy();
  }

  // UI handlers only flip signals; the effect above takes care of re-rendering
  selectTab(tab: 'temperature' | 'precipitation' | 'wind') {
    this.store.selectedTab.set(tab);
  }
  toggleUnit(unit: TempUnit) {
    if (this.store.unit() !== unit) {
      this.store.setAndSaveUnit(unit);
    }
  }

  /** Render (or re-render) using 24 data points */
  private render(labels: string[], values: number[]) {
    const tab = this.store.selectedTab();
    const unit = this.store.unit();

    const interval = Math.max(1, Math.round(labels.length / 10));

    const lineColors: Record<typeof tab, string> = {
      temperature: '#F55600',
      precipitation: '#3B82F6',
      wind: '#22C55E',
    };
    const lineColor = lineColors[tab];

    const fontSizeInRem = 0.75;

    const fontSizeInPx = this.calculateFontSizeInPixels(fontSizeInRem);

    const lineWidthInRem = 0.06;
    const lineWidthInPx = this.calculateFontSizeInPixels(lineWidthInRem);

    const topPaddingInRem = 1;
    const topPaddingInPx = this.calculateFontSizeInPixels(topPaddingInRem);

    // const yTitle = tab === 'temperature' ? `°${unit}` : tab === 'precipitation' ? 'mm' : 'km/h';

    // Unit text for labels
    const unitSymbol = tab === 'temperature' ? `°${unit}` : tab === 'precipitation' ? ' mm' : ' km/h';

    // Horizontal legend" at the top
    const topLabelsPlugin = {
      id: 'topLabels',
      afterDatasetsDraw: (chart) => {
        const { ctx, scales } = chart;
        const xScale = scales.x;
        ctx.save();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.font = `100 ${fontSizeInPx}px "RoobertENEL", sans-serif`;
        ctx.fillStyle = '#667790';
        for (let i = 0; i < labels.length; i++) {
          if (i % interval !== 0) continue; // only at interval marks
          const x = xScale.getPixelForValue(i);
          ctx.fillText(`${values[i]}${unitSymbol}`, x, 0);
        }
        ctx.restore();
      },
    };

    const maxVal = Math.max(...values);
    const minVal = Math.min(...values);
    const headroom = Math.max(1, (maxVal - minVal) * 0.18);

    const pixelRatio = Math.max(1, Math.round(window.devicePixelRatio || 1));

    const options: ChartOptions<'line'> = {
      responsive: true,
      animation: { duration: 200 },
      maintainAspectRatio: false, // allow CSS height to control the canvas
      devicePixelRatio: pixelRatio,
      layout: {
        padding: {
          top: topPaddingInPx,
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
        datalabels: {
          // we draw labels at top via custom plugin instead
          display: false,
        },
      },
      scales: {
        x: {
          border: { display: false }, // remove bottom axis line
          grid: {
            // keep only vertical lines at interval marks
            color: (ctx) => ((ctx.index ?? 0) % interval === 0 ? '#C2CDDD' : 'rgba(0,0,0,0)'),
            lineWidth: (ctx) => ((ctx.index ?? 0) % interval === 0 ? lineWidthInPx : 0),
            // drawBorder: false
          },
          ticks: {
            autoSkip: false,
            maxRotation: 0,
            color: '#667790',
            font: { size: fontSizeInPx, weight: 400, family: '"RoobertENEL", sans-serif' },
            // show hour labels at interval marks
            callback: (_value, index) => (index % interval === 0 ? labels[index] : ''),
          },
        },
        y: {
          display: false, // remove the entire left axis
          grid: { display: false }, //remove all horizontal lines
          border: { display: false },
          ticks: { display: false },
          suggestedMax: maxVal + headroom,
        },
      },
    };

    const data = {
      labels,
      datasets: [
        {
          data: values,
          fill: false,
          borderWidth: 2.5,
          borderColor: lineColor,
          pointRadius: (ctx) => (ctx.dataIndex % interval === 0 ? 2 : 0), // dots only at interval marks
          pointHoverRadius: 0,
          pointBackgroundColor: '#FF9861',
          pointBorderColor: '#FF9861',
          tension: 0.35,
        },
      ],
    };

    this.chart?.destroy();
    this.chart = new Chart(this.canvas.nativeElement.getContext('2d')!, {
      type: 'line',
      data,
      options,
      plugins: [topLabelsPlugin],
    });
  }

  private calculateFontSizeInPixels(fontSizeInRem: number): number {
    const topLabelFontSizeInRem = fontSizeInRem;

    const remInPx = parseFloat(getComputedStyle(document.documentElement).fontSize);

    // 3. Calculate the final font size in pixels.
    return topLabelFontSizeInRem * remInPx;
  }
}
